// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// tutorial.ts — eight steps on the real board, as DATA.
//
// IT DRIVES THE SHIPPED STATE MACHINE. There is no mock colony, no parallel rule set and no scripted
// animation: `setup()` builds a genuine `State` with the real `createState`, and every step is
// satisfied by the player issuing a genuine command down the same path a real winter uses. A tutorial
// with a private implementation is a lie the moment anybody touches the rules — and worse, it is not
// a DETECTABLE lie: it goes on confidently teaching a game that no longer exists. Because the steps
// are data, tests/tutorial.test.ts replays every one through the real simulation and asserts each is
// legal and reachable from the state the previous one produced.
//
// STEP ONE IS A SINGLE TAP WITH AN INSTANT PAYOFF. An earlier draft opened with "tap Bregg, then tap
// the larder" — a compound gesture, and because the tutorial is paused between steps it produced no
// motion at all, so the first thing a new player ever did appeared to do nothing.
//
// STEP EIGHT IS THE POINT. Everything before it is scaffolding to reach the moment the player has to
// promote TEND above COOK, knowing the only person who can tend the sick is also the only cook. Both
// answers cost something. That is the game, and it is not a thing anybody learns from a rules card.

import { Cls, Cmd, Command, Desig, State, createState, rankOf } from './sim';
import { CORRIDOR_T, LARDER_T } from './sim';
import { COLS, idx } from './map';
import { Build } from './modes';

/** What the tutorial can see of the interface, so a step can be about a VIEW as well as a move. */
export interface Ctx {
  selected: number;
  view: string;
  focus: number;
}

export interface Step {
  id: string;
  /** One sentence, imperative, naming what to touch. */
  text: string;
  /** Said once the step is done — the WHY, only after the player has seen the what. */
  after: string;
  /** A tile to ring on the board, or -1. */
  hint(s: State): number;
  /** Is this command allowed right now? Everything else is refused without breaking the sequence. */
  allows(s: State, c: Command): boolean;
  /** Does the step run the clock, or is it a paused instruction? */
  runs?: boolean;
  done(s: State, ctx: Ctx): boolean;
}

/** The doorway's row. The corridor tile is the middle one; the walls sit either side of the door. */
export const CORRIDOR_ROW = [idx(4, 7), idx(5, 7), idx(6, 7)];

/**
 * The tutorial's colony: the real Longwinter map at a fixed seed, with one colonist already ill so
 * step 8's dilemma is real rather than described.
 */
export function setup(): State {
  const s = createState(20260821, 'longwinter');
  s.ill[1] = 1;
  return s;
}

const desigOn = (s: State, t: number, k: Desig) => s.desig[t] === k;

export const STEPS: readonly Step[] = [
  {
    id: 'select',
    text: 'Tap Bregg — the pale dot marked B, down in the house.',
    after: 'Selecting somebody dims the map to the ground they can actually reach. That shading is the whole game in one picture.',
    hint: (s) => s.pos[0],
    allows: () => false,
    done: (_s, ctx) => ctx.selected === 0,
  },
  {
    id: 'call',
    text: 'Now tap the larder — the amber store up the corridor — to send him there.',
    after: 'That is a one-off order, not a policy. You will spend almost the whole game setting policy instead.',
    hint: () => LARDER_T,
    allows: (_s, c) => c.kind === Cmd.Call && c.args[1] === LARDER_T,
    done: (s) => s.callTo[0] === LARDER_T || s.pos[0] === LARDER_T,
  },
  {
    id: 'play',
    text: 'Press play and watch him go.',
    after: 'Deep snow is slow to cross — a step onto it costs more the deeper it is. Distance here is measured in effort, not tiles.',
    hint: () => LARDER_T,
    allows: () => false,
    runs: true,
    done: (s) => s.pos[0] === LARDER_T || s.tick > 400,
  },
  {
    id: 'clear',
    text: 'Press and hold the drift in the doorway, then choose Clear.',
    after: 'You did not order anyone to dig. You put the work on the map — somebody will pick it up.',
    hint: () => CORRIDOR_T,
    allows: (_s, c) => c.kind === Cmd.Designate && c.args[2] === Desig.Clear,
    done: (s) => desigOn(s, CORRIDOR_T, Desig.Clear) || s.depth[CORRIDOR_T] === 0,
  },
  {
    id: 'rank',
    text: 'Open WORK and tap the up-arrow beside CLEAR until it is top of the list.',
    after: 'That list is your only real instruction to the colony. Everyone picks their next job from it — and from how far they must walk.',
    hint: () => -1,
    allows: (_s, c) => c.kind === Cmd.Rank,
    done: (s) => rankOf(s, Cls.Clear) === 0,
  },
  {
    id: 'field',
    text: 'Tap the larder again to shade the map by how far every tile is from your food.',
    after: 'When a drift closes that corridor the larder does not vanish — it just goes from four tiles away to unreachable, and the haulers quietly stop going.',
    hint: () => LARDER_T,
    allows: () => false,
    done: (_s, ctx) => ctx.view === 'cost' && ctx.focus === LARDER_T,
  },
  {
    // THE STEP THAT TAUGHT NOTHING, TWICE. It first read "widen the corridor to three tiles", which
    // is wrong about this map — the chokepoint is the DOORWAY, walled on both sides, and the tiles
    // either side of the drift are already open. Worse, its completion test counted a tile with no
    // snow as "already cleared", so the step passed the instant it appeared without the player doing
    // anything at all. The lesson that actually applies here is the DURABLE fix rather than the
    // repeated one: a path takes snow at half the rate, so laying one down the corridor is how you
    // stop digging the same tile out every storm.
    id: 'path',
    text: 'Storm coming. Open BUILD, choose Path, then tap the corridor to lay one down.',
    after: 'Snow falls on a path at half the rate, and it drifts hardest against walls — which is why this one doorway silts up faster than open ground.',
    hint: () => CORRIDOR_T,
    allows: (_s, c) =>
      c.kind === Cmd.Designate && (c.args[2] === Desig.Clear || (c.args[2] === Desig.Build && c.args[3] === Build.Path)),
    done: (s) =>
      (s.desig[CORRIDOR_T] === Desig.Build && s.desigArg[CORRIDOR_T] === Build.Path) ||
      s.build[CORRIDOR_T] === Build.Path + 1,
  },
  {
    id: 'triage',
    text: 'Maret is ill and only a healer can tend her — and your healer is also your cook. Put TEND above COOK.',
    after: 'Dinner is now late. That is the game: you never have enough hands, and every ranking you set is a thing you have chosen to stop doing.',
    hint: (s) => s.pos[1],
    allows: (_s, c) => c.kind === Cmd.Rank || c.kind === Cmd.Forbid || c.kind === Cmd.Call,
    done: (s) => rankOf(s, Cls.Tend) < rankOf(s, Cls.Cook),
  },
];

export const STEP_COUNT = STEPS.length;

/** Is this command permitted at step `i`? An out-of-range index allows everything. */
export function allowed(i: number, s: State, c: Command): boolean {
  const step = STEPS[i];
  return step ? step.allows(s, c) : true;
}

export { COLS };
